import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Icon,
  Text as ChakraText,
} from '@chakra-ui/react'
import React, { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { AiOutlineUsergroupDelete } from 'react-icons/ai'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../ui-kit'
import useToast from '../../hooks/useToast'
import type { UserErrorResponse, UserSessionResponse } from '../../interfaces'

interface Props {
  label?: string
  id?: string
  user: UserSessionResponse | null
  isOpen: boolean
  onClose(): void
  getData(): Promise<void>
}

const DeleteUserGroupModal = ({
  label,
  id,
  user,
  isOpen,
  onClose,
  getData,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()
  const [formError, setFormError] = useState<string>('')
  const { request, loading } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Icon
            as={AiOutlineUsergroupDelete}
            color={colors.appBattleshipGrey}
            backgroundColor={colors.appPaleGrey}
            boxSize={6}
            padding="0.1em"
            borderRadius="50%"
            marginRight="0.5em"
          />
          {t('delete_user_group', 'Delete user group')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {formError ? (
            <Alert status="error" mb="1em">
              <AlertIcon />
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          ) : null}
          <Trans t={t} i18nKey="delete_user_group_modal_description">
            <ChakraText color={colors.appBattleshipGrey}>
              This will delete the user group {{ label }} and revoke access to
              any shared assets to users through this group. Once submitted,
              this action cannot be reversed.
            </ChakraText>
          </Trans>
        </ModalBody>
        <ModalFooter
          borderTop={`1px solid ${colors.appGrey}`}
          style={{ gap: '1em' }}
        >
          <Button
            onClick={() => {
              onClose()
              setFormError('')
            }}
            variant="outline"
          >
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            colorScheme="appRed"
            type="submit"
            isSubmitting={loading}
            isDisabled={loading}
            onClick={async () => {
              setFormError('')
              const response = (await request.delete(
                `/api/clients/${user?.clientNumber}/groups/${id}`,
              )) as null | UserErrorResponse

              if (request.error) {
                setFormError(
                  (response as UserErrorResponse).message ??
                    t(
                      'unknown_error_try_again',
                      'An unknown error occured, please try again.',
                    ),
                )
              } else {
                toast({
                  description: t(
                    'successfully_deleted',
                    'Successfully deleted',
                  ),
                  status: 'success',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })
                await getData()
                onClose()
              }
            }}
          >
            {t('submit', 'Submit')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default DeleteUserGroupModal
